import { z } from 'zod';

import { MINIMUM_BIRTH_YEAR, ZIPCODE_VALIDATION_REGEX } from '@/utils/constants';
import { getAge } from '@/utils/stringValidators';
import { getProductMinAge } from '@/utils/productHelpers';
import { parseDate } from '@/utils/stringUtils';

export type CoverageFieldsErrorMessages = {
  firstName: string;
  lastName: string;
  zipCode: string;
  dateOfBirth: string;
  employeeId: string;
};

// Shared by ConfirmDetails and CoverageCheckWithEmail - same fields and rules, different messages.
export const createCoverageFieldsSchema = (errorMessages: CoverageFieldsErrorMessages) => z.object({
  firstName: z.string({ required_error: errorMessages.firstName })
    .min(1, { message: errorMessages.firstName }),
  lastName: z.string({ required_error: errorMessages.lastName })
    .min(1, { message: errorMessages.lastName }),
  zipCode: z.union([
    z.literal(''),
    z.string({ message: errorMessages.zipCode })
      .regex(ZIPCODE_VALIDATION_REGEX, errorMessages.zipCode),
  ]).optional(),
  dateOfBirth: z.string({
    required_error: errorMessages.dateOfBirth,
    // 14 characters to account for '/' and spaces
  }).length(14, { message: errorMessages.dateOfBirth })
    .refine((dob) => {
      // check that it is parsable as a date
      const parsedDate = parseDate(dob);
      const date = new Date(parsedDate);
      const isValidDate = !Number.isNaN(date.getTime());

      if (!isValidDate) {
        return false;
      }

      return date.getFullYear() >= MINIMUM_BIRTH_YEAR
          && (getAge(dob) >= getProductMinAge('sleepio'));
    }, { message: errorMessages.dateOfBirth }),
  employeeId: z.union([
    z.literal(''),
    z.string({ required_error: errorMessages.employeeId }),
  ]).optional(),
});
